import { api } from "@/api/api";
import type { UserFormInputFieldType } from "@/types/user-form";
import { initializeUuidWithLock } from "@/utils/hooks/usePersistentCartInit";
import { CART_SHARE_UUID } from "@/utils/constants/cartShare";
import axios from "axios";
import type { TrustPilotPersistedBusinessPayload } from "@/api/reviewTrustPilotApi";
import { reportToFlare } from "@/utils/reportToFlare";
import {
  isPlannedMaintenanceError,
  isTransientNetworkError,
  retryIdempotentRequest,
} from "@/utils/http/serviceAvailability";
import { REPUTECH_DOMAIN } from "@/utils/constants/brand";
import type { CouponType } from "@/types/trustpilotPricing";

export type CartShareUuidResponse = {
  uuid: string;
  created_at: string;
};

export type PersistedCartAffiliateId = number | string;
export type PersistedCartAffiliateFunnel = string;

export type PersistedCartUser = Partial<UserFormInputFieldType> & {
  businessCustomerConfirmed?: boolean;
  termsAccepted?: boolean;
  reviewSubmissionAuthorized?: boolean;
  successBasedBillingAccepted?: boolean;
};

export type PersistedGetCartResponse<
  TPersistedCartData = PersistedCartDataTrustPilot,
> = {
  success: boolean;
  data: {
    uuid: string;
    payload: TPersistedCartData;
    created_at: string;
    updated_at: string;
  };
};

export type PersistedCartPayloadTrustPilot = {
  payload: PersistedCartDataTrustPilot;
};

export type PersistedCartDataTrustPilot = {
  // Persistent Trustpilot carts keep full business objects so the funnel can
  // restore selected company context, pricing inputs, and selected reviews
  // without serializing the client-only userSelectedReviews field.
  businesses: TrustPilotPersistedBusinessPayload[];
  user: PersistedCartUser;
  coupon_code?: string;
  coupon_data?: CouponType;
  affiliate_id?: PersistedCartAffiliateId;
  affiliate_funnel?: PersistedCartAffiliateFunnel;
};

const isMissingPersistentCartError = (error: unknown) =>
  axios.isAxiosError(error) &&
  (error.response?.status === 404 || error.response?.status === 410);

class PersistentCartUnavailableError extends Error {
  constructor() {
    super("Persistent cart UUID unavailable after reinitialization");
    this.name = "PersistentCartUnavailableError";
  }
}

export const isPersistentCartUnavailableError = (error: unknown) =>
  error instanceof Error && error.name === "PersistentCartUnavailableError";

const readStoredPersistentCartUuid = () => {
  const storedData = localStorage.getItem(CART_SHARE_UUID);
  return storedData ? JSON.parse(storedData).uuid : null;
};

export const waitForStoredPersistentCartUuid = async (maxAttempts = 15) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const uuid = readStoredPersistentCartUuid();

    if (uuid) {
      return uuid;
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return null;
};

export const persistentCartApi = {
  async getUuid() {
    try {
      const response = await api.get<CartShareUuidResponse>(
        "/persistent-cart/get-uuid",
        { timeout: 10_000, params: {frontend_domain: REPUTECH_DOMAIN} },
      );
      return response.data;
    } catch (e) {
      if (
        isMissingPersistentCartError(e) ||
        isPlannedMaintenanceError(e) ||
        isTransientNetworkError(e)
      ) {
        return undefined;
      }

      throw e;
    }
  },

  async patchCartData(
    uuid: string,
    payload: PersistedCartPayloadTrustPilot,
  ) {
    try {
      // Try to make the request with the current uuid
      const response = await api.patch(`/persistent-cart/${uuid}`, payload, {
        params: {frontend_domain: REPUTECH_DOMAIN},
      });
      return response.data;
    } catch (e) {
      // Only retry for UUID-related errors
      if (isMissingPersistentCartError(e)) {
        // If request fails, delete old UUID and initialize a new one, then retry once
        try {
          // Remove the stale UUID from localStorage
          localStorage.removeItem(CART_SHARE_UUID);

          // Initialize a new UUID
          await initializeUuidWithLock();
          const newUuid = await waitForStoredPersistentCartUuid();

          if (!newUuid) {
            throw new PersistentCartUnavailableError();
          }

          // Retry the request with the new uuid (only once to prevent infinite loop)
          const retryResponse = await api.patch(
            `/persistent-cart/${newUuid}`,
            payload,
            {params: {frontend_domain: REPUTECH_DOMAIN}},
          );
          return retryResponse.data;
        } catch (retryError) {
          if (isPersistentCartUnavailableError(retryError)) {
            throw retryError;
          }

          reportToFlare(retryError, "persistent_cart_patch_retry");
          throw new Error("Failed to update cart after UUID reinitialization", {
            cause: retryError,
          });
        }
      } else {
        throw e; // Propagate other errors
      }
    }
  },

  /**
   * Callers must treat undefined as an unavailable persisted cart. Missing or
   * expired cart UUIDs are expected and should fall back locally without noise.
   */
  async getCartData<
    TPersistedCartData = PersistedCartDataTrustPilot,
  >(
    uuid: string,
  ): Promise<PersistedGetCartResponse<TPersistedCartData> | undefined> {
    try {
      const response = await retryIdempotentRequest(
        () =>
          api.get<PersistedGetCartResponse<TPersistedCartData>>(
            `/persistent-cart/${uuid}`,
            { timeout: 10_000, params: {frontend_domain: REPUTECH_DOMAIN} },
          ),
        { delays: [500] },
      );

      return response.data;
    } catch (e) {
      if (
        isMissingPersistentCartError(e) ||
        isPlannedMaintenanceError(e) ||
        isTransientNetworkError(e)
      ) {
        return undefined;
      }

      reportToFlare(e, "persistent_cart_get");
      return undefined;
    }
  },
  async deleteCart(uuid: string) {
    try {
      const response = await api.delete(`/persistent-cart/${uuid}`, {
        params: {frontend_domain: REPUTECH_DOMAIN},
      });

      return response.data;
    } catch (e) {
      throw e;
    }
  },
};
